import { useEffect, useState } from 'react';
import { InputAdornment, Box, Button, Grid } from '@material-ui/core';
import { InputPassword, PasswordStrength, Backdrop } from '@room-match/shared-ui-components';
import { FormikProps, useFormik } from 'formik';
import { Link, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';
import { FrontLayout } from 'components';
import { PasswordIcon } from 'components/Icons';
import { IResetPasswordRequestPayload } from 'shared/interfaces/IUser';
import { selectUserState, userResetPassword } from 'shared/redux/slicers/user.slicer';
import { getErrorMessage } from 'shared/utils/getErrorMessage';
import { PasswordPrinciple, validatePassword } from 'shared/utils/passwordUtil';
import { ROUTES } from 'shared/constants/ROUTES';

import { useStyles } from './ResetPassword.styles';

const ResetPassword = () => {
  const { search = '' } = window.location;
  const urlParams = new URLSearchParams(search);
  const token = urlParams.get('reset_password_token');
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const [, setPasswordValidationResult] = useState<PasswordPrinciple | null>(null);
  const [password_str, setPasswordStr] = useState<string>('');

  const { isLoading, isLoggedIn } = useSelector(selectUserState);

  useEffect(() => {
    if (isLoggedIn) {
      history.push(ROUTES.CASTING.PROJECTS.PROJECT_LIST);
    }
  }, [isLoggedIn, history]);

  // Forms
  const initialValues: IResetPasswordRequestPayload = {
    reset_password_token: token || '',
    password: '',
    password_confirmation: '',
  };

  const resetPasswordValidationSchema: yup.SchemaOf<IResetPasswordRequestPayload> = yup.object({
    reset_password_token: yup.string().required('Token is required'),
    password: yup
      .string()
      .required('Password is required')
      .min(8, 'Password must be atleast 8 characters')
      .test('passwordValidate', 'Invalid password', (value: any) => {
        if (value) {
          const validatePasswordResult = validatePassword(value);
          setPasswordValidationResult(validatePasswordResult);
          return validatePasswordResult.IsValid;
        }
        return false;
      }),
    password_confirmation: yup
      .string()
      .required('Password confirmation is required')
      .oneOf([yup.ref('password')], 'Your passwords do not match.'),
  });

  const handleSignUpSubmit = async (values: IResetPasswordRequestPayload) => {
    dispatch(userResetPassword(values, history));
  };

  const form: FormikProps<IResetPasswordRequestPayload> = useFormik({
    initialValues,
    validationSchema: resetPasswordValidationSchema,
    onSubmit: (values) => handleSignUpSubmit(values),
  });

  return (
    <FrontLayout containerWidth={400}>
      <Box>
        <Grid container spacing={2}>
          <Grid item>
            <Box className={classes.boxContainer}>
              <InputPassword
                variant={'outlined'}
                margin={'normal'}
                name="password"
                placeholder="New Password"
                onChange={(e) => {
                  if (form.errors.password && !form.touched.password) {
                    form.setFieldTouched('password');
                    form.validateField('password');
                  }
                  setPasswordStr(e.target.value || '');
                  return form.handleChange(e);
                }}
                errorMessage={getErrorMessage(form.touched.password, form.errors.password)}
                value={form.values.password}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PasswordIcon style={{ marginTop: '5px' }} />
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ tabIndex: 8 }}
                onCopy={(e) => {
                  e.preventDefault();
                  return false;
                }}
                onPaste={(e) => {
                  e.preventDefault();
                  return false;
                }}
              />
              <InputPassword
                variant={'outlined'}
                margin={'normal'}
                name="password_confirmation"
                placeholder="Confirm New Password"
                fullWidth
                onChange={(e) => {
                  if (form.errors.password_confirmation && !form.touched.password_confirmation) {
                    form.setFieldTouched('password_confirmation');
                    form.validateField('password_confirmation');
                  }
                  return form.handleChange(e);
                }}
                errorMessage={getErrorMessage(form.touched.password_confirmation, form.errors.password_confirmation)}
                value={form.values.password_confirmation}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PasswordIcon style={{ marginTop: '5px' }} />
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ tabIndex: 8 }}
                onCopy={(e) => {
                  e.preventDefault();
                  return false;
                }}
                onPaste={(e) => {
                  e.preventDefault();
                  return false;
                }}
              />
              <PasswordStrength password={password_str} />
            </Box>
            <Box className={classes.boxContainer}>
              <Button
                color="primary"
                variant="contained"
                disableElevation
                fullWidth
                tabIndex={10}
                onClick={() => form.handleSubmit()}
                className={classes.buttonCls}
              >
                Update Password
              </Button>
              <Button
                variant="outlined"
                disableElevation
                fullWidth
                component={Link}
                to={'/login'}
                tabIndex={11}
                className={classes.buttonCls}
              >
                Cancel
              </Button>
            </Box>
          </Grid>
          <Grid>
            <Backdrop isLoading={isLoading} />
          </Grid>
        </Grid>
      </Box>
    </FrontLayout>
  );
};

export default ResetPassword;
